import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type RowSelectionState,
  type SortingState,
  type ExpandedState,
} from '@tanstack/react-table'
import { ChevronDown, ChevronRight, ChevronsUpDown, ChevronUp, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Checkbox } from '../Checkbox.tsx'
import { EmptyState } from '../EmptyState.tsx'
import { ErrorState } from '../ErrorState.tsx'
import { Input } from '../Input.tsx'
import { LoadingState } from '../LoadingState.tsx'
import { DataTablePagination } from './DataTablePagination.tsx'
import type { DataTableProps } from './types.ts'

const SELECT_COLUMN_ID = '__select__'
const EXPAND_COLUMN_ID = '__expand__'

function buildSelectColumn<TRow>(): ColumnDef<TRow, unknown> {
  return {
    id: SELECT_COLUMN_ID,
    enableSorting: false,
    size: 36,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={table.getIsSomePageRowsSelected()}
        onChange={(e) => table.toggleAllPageRowsSelected(e.currentTarget.checked)}
        aria-label="Alle auswählen"
        wrapperClassName="align-middle"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={(e) => row.toggleSelected(e.currentTarget.checked)}
        aria-label="Zeile auswählen"
        wrapperClassName="align-middle"
        onClick={(e) => e.stopPropagation()}
      />
    ),
  }
}

function buildExpandColumn<TRow>(): ColumnDef<TRow, unknown> {
  return {
    id: EXPAND_COLUMN_ID,
    enableSorting: false,
    size: 36,
    header: () => null,
    cell: ({ row }) =>
      row.getCanExpand() ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            row.toggleExpanded()
          }}
          aria-label={row.getIsExpanded() ? 'Zuklappen' : 'Aufklappen'}
          className="p-1 text-gray-500 hover:text-gray-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          {row.getIsExpanded() ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
      ) : null,
  }
}

/**
 * Zentrale Tabellen-Komponente.
 * Gebaut auf `@tanstack/react-table` (headless) + Tailwind-Styles.
 * Features: Sortieren, Filtern, Paginieren, Zeilenauswahl, Expandable-Rows.
 */
export function DataTable<TRow>({
  data,
  columns,
  getRowId,
  loading,
  error,
  onRetry,
  emptyState,
  pagination,
  pageSize: propPageSize,
  totalCount,
  currentPage,
  onPageChange,
  enableRowSelection,
  selectedRowIds,
  onSelectedRowIdsChange,
  enableSorting = true,
  renderExpandedRow,
  getRowCanExpand,
  expandedRowIds,
  onExpandedChange,
  onRowClick,
  rowClassName,
  toolbar,
  className,
  stickyHeader = true,
  searchable = true,
  searchPlaceholder = 'Suchen …',
  globalFilter,
  onGlobalFilterChange,
  filteredEmptyLabel,
  initialSorting,
}: DataTableProps<TRow>) {
  const [sorting, setSorting] = useState<SortingState>(() => initialSorting ?? [])
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({})
  const isExpandControlled = expandedRowIds !== undefined
  const expanded: ExpandedState = isExpandControlled
    ? Object.fromEntries(expandedRowIds!.map((id) => [id, true]))
    : internalExpanded
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() =>
    Object.fromEntries((selectedRowIds ?? []).map((id) => [id, true])),
  )
  const [internalSearch, setInternalSearch] = useState('')
  const isFilterControlled = globalFilter !== undefined
  const effectiveFilter = isFilterControlled ? globalFilter : internalSearch

  useEffect(() => {
    if (!selectedRowIds) return
    setRowSelection(Object.fromEntries(selectedRowIds.map((id) => [id, true])))
  }, [selectedRowIds])

  const allColumns = useMemo<ColumnDef<TRow, unknown>[]>(() => {
    const cols: ColumnDef<TRow, unknown>[] = []
    if (enableRowSelection) cols.push(buildSelectColumn<TRow>())
    if (renderExpandedRow) cols.push(buildExpandColumn<TRow>())
    cols.push(...columns)
    return cols
  }, [columns, enableRowSelection, renderExpandedRow])

  const isServerPagination = totalCount !== undefined && onPageChange !== undefined
  const clientPageSize = typeof pagination === 'object' ? (pagination.pageSize ?? 25) : 25
  const effectivePageSize = propPageSize ?? (isServerPagination ? 25 : clientPageSize)
  const paginationEnabled = Boolean(pagination) || isServerPagination

  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      sorting,
      expanded,
      rowSelection,
      globalFilter: effectiveFilter,
      ...(isServerPagination
        ? { pagination: { pageIndex: (currentPage ?? 1) - 1, pageSize: effectivePageSize } }
        : {}),
    },
    manualPagination: isServerPagination ? true : false,
    rowCount: isServerPagination ? totalCount : undefined,
    onGlobalFilterChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? (updater as (v: string) => string)(effectiveFilter ?? '')
          : ((updater as string) ?? '')
      if (isFilterControlled) {
        onGlobalFilterChange?.(next)
      } else {
        setInternalSearch(next)
      }
    },
    onSortingChange: setSorting,
    onExpandedChange: (updater) => {
      if (isExpandControlled) {
        // Controlled mode: derive id+newValue and bubble up.
        if (!onExpandedChange) return
        const next = typeof updater === 'function' ? updater(expanded) : updater
        if (typeof next !== 'object') return
        const prevIds = new Set(expandedRowIds ?? [])
        const nextIds = new Set(Object.keys(next).filter((k) => (next as Record<string, boolean>)[k]))
        for (const id of nextIds) if (!prevIds.has(id)) onExpandedChange(id, true)
        for (const id of prevIds) if (!nextIds.has(id)) onExpandedChange(id, false)
        return
      }
      setInternalExpanded(updater)
    },
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        if (onSelectedRowIdsChange) {
          onSelectedRowIdsChange(Object.keys(next).filter((k) => next[k]))
        }
        return next
      })
    },
    onPaginationChange: isServerPagination
      ? (updater) => {
          const prev = { pageIndex: (currentPage ?? 1) - 1, pageSize: effectivePageSize }
          const next = typeof updater === 'function' ? updater(prev) : updater
          onPageChange!(next.pageIndex + 1)
        }
      : undefined,
    getRowId: getRowId ? (row, index) => getRowId(row, index) : undefined,
    getRowCanExpand: getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: renderExpandedRow ? getExpandedRowModel() : undefined,
    getPaginationRowModel: paginationEnabled ? getPaginationRowModel() : undefined,
    initialState:
      paginationEnabled && !isServerPagination
        ? { pagination: { pageIndex: 0, pageSize: effectivePageSize } }
        : undefined,
    enableRowSelection: Boolean(enableRowSelection),
    enableSorting,
  })

  const hasAnyRows = table.getRowModel().rows.length > 0
  const hasSourceData = data.length > 0
  const showSearch = searchable
  const showToolbar = showSearch || Boolean(toolbar)

  const searchInput = showSearch ? (
    <div className="relative w-full max-w-xs">
      <Input
        type="text"
        value={effectiveFilter ?? ''}
        onChange={(e) => {
          const next = e.target.value
          if (isFilterControlled) {
            onGlobalFilterChange?.(next)
          } else {
            setInternalSearch(next)
          }
        }}
        placeholder={searchPlaceholder}
        leadingIcon={<Search className="h-4 w-4" />}
        aria-label={searchPlaceholder}
        wrapperClassName="w-full"
      />
      {effectiveFilter ? (
        <button
          type="button"
          onClick={() => {
            if (isFilterControlled) {
              onGlobalFilterChange?.('')
            } else {
              setInternalSearch('')
            }
          }}
          aria-label="Suche leeren"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  ) : null

  return (
    <div
      className={cn(
        'bg-white border border-gray-200 rounded-lg shadow-card overflow-hidden flex flex-col',
        className,
      )}
    >
      {showToolbar ? (
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          {searchInput}
          {toolbar ? <div className="flex items-center gap-2 flex-wrap">{toolbar}</div> : null}
        </div>
      ) : null}

      <div className="relative overflow-x-auto">
        <table className="w-full text-small">
          <thead
            className={cn(
              'bg-gray-50 text-caption font-semibold uppercase tracking-wide text-gray-500',
              stickyHeader && 'sticky top-0 z-10',
            )}
          >
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-gray-200">
                {hg.headers.map((header) => {
                  const canSort = enableSorting && header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() || undefined }}
                      className="text-left px-3 py-2 align-middle"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-gray-700"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDir === 'asc' ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : sortDir === 'desc' ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={allColumns.length} className="p-0">
                  <LoadingState />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={allColumns.length} className="p-0">
                  <ErrorState description={error} onRetry={onRetry} />
                </td>
              </tr>
            ) : !hasAnyRows ? (
              <tr>
                <td colSpan={allColumns.length} className="p-0">
                  {!hasSourceData
                    ? (emptyState ?? <EmptyState title="Keine Daten" />)
                    : (filteredEmptyLabel ?? (
                        <EmptyState
                          title="Keine Treffer"
                          description="Keine Einträge entsprechen den aktuellen Filtern."
                        />
                      ))}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row: Row<TRow>) => (
                <Fragment key={row.id}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      'bg-white',
                      onRowClick && 'hover:bg-gray-50 cursor-pointer',
                      row.getIsSelected() && 'bg-brand-50/50',
                      rowClassName?.(row.original),
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 align-middle text-gray-800"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {renderExpandedRow && row.getIsExpanded() ? (
                    <tr className="bg-gray-50">
                      <td colSpan={row.getVisibleCells().length} className="p-3">
                        {renderExpandedRow(row)}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {paginationEnabled && hasAnyRows ? (
        <DataTablePagination
          table={table}
          totalRowCount={isServerPagination ? totalCount : undefined}
        />
      ) : null}
    </div>
  )
}
